import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { TenantConfiguration } from '@parashift/shared/models';
import { ErrorResponse, NotificationService, Logger, JsonApiQueryData, TenantConfigurationService, SessionStore } from '@parashift/shared/services';
import { CheckPermissionsDirective } from '@parashift/shared/directives';
import { IterateObjectPipe } from '@parashift/shared/pipes';
import { select } from '@parashift/shared/utils';
import { ModalService } from '@parashift/shared/components';
import { GeneralFormComponent } from './forms/general-form/general-form.component';
import { DangerZoneFormComponent } from './forms/danger-zone-form/danger-zone-form.component';
import { HeaderActionStore } from '@parashift/shared/stores';

@Component({
  selector: 'tenant-configuration',
  templateUrl: './tenant-configuration.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    CheckPermissionsDirective,
    DangerZoneFormComponent,
    GeneralFormComponent,
    IterateObjectPipe,
]
})
export class TenantConfigurationComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private sessionStore = inject(SessionStore);
  private tenantConfigurationService = inject(TenantConfigurationService);
  private modalService = inject(ModalService);
  private notificationService = inject(NotificationService);
  private headerActionStore = inject(HeaderActionStore);
  private logger = inject(Logger);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  @ViewChild(GeneralFormComponent, { static: false }) set generalFormComponent(formComponent: GeneralFormComponent) {
    this.formRefs.general = formComponent;
  }

  @ViewChild(DangerZoneFormComponent, { static: false }) set dangerZoneFormComponent(formComponent: DangerZoneFormComponent) {
    this.formRefs['danger-zone'] = formComponent;
  }

  readonly parashiftPowers$: Observable<boolean>;

  protected _configuration$ = new BehaviorSubject<TenantConfiguration>(undefined);
  readonly configuration$ = this._configuration$.asObservable();
  get configuration() { return this._configuration$.getValue(); }

  protected _model$ = new BehaviorSubject<TenantConfiguration['plainModel']>(undefined);
  readonly model$ = this._model$.asObservable();
  get model() { return this._model$.getValue(); }

  formRefs = {
    general: undefined,
    'danger-zone': undefined
  };

  id: string;
  tenant_id: number;
  error: ErrorResponse;
  disableForm = false;
  currentTab = 'general';
  tabs = {
    general: $localize `:@@components.tenant_configuration.tabs.general:General`,
    'danger-zone': $localize `:@@omponents.tenant_configuration.tabs.danger_zone:Danger Zone`,
  };

  private nextTab = undefined;

  constructor() {
    const destroyRef = this.destroyRef;
    this.parashiftPowers$ = this.sessionStore.state$.pipe(select(state => state.parashift_powers));
    destroyRef.onDestroy(() => this.headerActionStore.destroy());
  }

  ngOnInit() {
    this.initTabs();
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
      this.tenant_id = params.get('tenant_id') ? Number(params.get('tenant_id')) : undefined;
      this.getTenantConfiguration();
    });
  }

  switchTab(tab: string) {
    if (tab !== this.currentTab && this.formRefs[this.currentTab]?.form.dirty) {
      this.confirmLeavingWithoutPersisting(tab);
      return;
    }
    this.replaceUrl(tab);
  }

  saveTenantConfiguration(model: TenantConfiguration['plainModel']) {
    this.configuration.plainModel = model;

    if (!this.configuration.hasDirtyAttributes) {
      return;
    }

    this.disableForm = true;
    this.headerActionStore.toggleActionsState(this.disableForm);
    this.tenantConfigurationService.saveRecord(this.configuration, this.tenant_id).subscribe({
      next: (configuration: TenantConfiguration) => {
        this._configuration$.next(configuration);
        this._model$.next(this.configuration.plainModel);

        this.notificationService.success({
          description: $localize `:@@components.tenant_configuration.configuration_saved_description:The settings has been successfully saved.`,
          title: $localize `:@@components.tenant_configuration.configuration_saved_title:Settings saved`
        });

        this.disableForm = false;
        this.headerActionStore.toggleActionsState(this.disableForm);
        this.formRefs[this.currentTab].form.markAsPristine();
        this.formRefs[this.currentTab].form.markAsUntouched();

        if (this.nextTab) {
          this.replaceUrl(this.nextTab);
          this.nextTab = undefined;
        }
      },
      error: (error: ErrorResponse) => {
        this.logger.log('Save Configuration Error', error);
        this.notificationService.formattedErrorResponse(error);
        this.disableForm = false;
        this.headerActionStore.toggleActionsState(this.disableForm);
      }
    });
  }

  private initTabs() {
    this.currentTab = this.route.snapshot.queryParamMap.get('tab') || 'general';
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
      this.currentTab = params.get('tab') || 'general';
      this.cdr.detectChanges();
    });
  }

  private getTenantConfiguration() {
    this.tenantConfigurationService.findAll(undefined, this.tenant_id).subscribe({
      next: (configurations: JsonApiQueryData<TenantConfiguration>) => {
        const configuration = configurations.getModels()[0];
        if (configuration) {
          this.id = configuration.id;
          this.listenForParashiftPowersChanges();
          this.setTenantConfiguration(configuration);
        }
      },
      error: (error: ErrorResponse) => {
        this.id = null;
        this.error = error;
        this.cdr.detectChanges();
        this.logger.log('tenant-configuration-component', 'TENANT CONFIGURATION Error', error);
        this.notificationService.formattedErrorResponse(error);
      }
    });
  }

  private setTenantConfiguration(configuration: TenantConfiguration) {
    this._model$.next(configuration.plainModel);
    this._configuration$.next(configuration);
  }

  private confirmLeavingWithoutPersisting(tab: string) {
    const initialState = {
      title: $localize `:@@components.tenant_configuration.leave_unsaved_form_modal.title:Un-saved changes`,
      description: $localize `:@@components.tenant_configuration.leave_unsaved_form_modal.description:The form contains un-saved changes. Do you want to leave this form without saving?`,
      button_cancel: $localize `:@@components.tenant_configuration.leave_unsaved_form_modal.button_cancel:Discard`,
      button_confirm: $localize `:@@components.tenant_configuration.leave_unsaved_form_modal.button_save:Save`,
      emitCancelEvent: true
    };
    this.modalService.confirmation({ initialState })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (response) {
          if (this.formRefs[this.currentTab]?.form.valid) {
            this.nextTab = tab;
          }
          this.formRefs[this.currentTab]?.submit();
        } else {
          this.formRefs[this.currentTab]?.form.markAsPristine();
          this.formRefs[this.currentTab]?.form.markAsUntouched();
          this.replaceUrl(tab);
        }
      });
  }

  private replaceUrl(tabValue: string) {
    const tab = { tab: tabValue || undefined };
    this.router.navigate(
      ['.'],
      { queryParams: tab, relativeTo: this.route, queryParamsHandling: 'merge' }
    );

    this.cdr.detectChanges();
  }

  private listenForParashiftPowersChanges() {
    this.parashiftPowers$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(enabled => {
        if (enabled) {
          this.headerActionStore.addActions({
            saveButton: { dispatch: () => this.configuration && this.formRefs[this.currentTab]?.submit() },
          });
        } else {
          this.headerActionStore.removeActions(['saveButton']);
        }
      });
  }
}
